import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import type React from 'react';

import { LoginForm } from '@/pages/(auth)/signin/LoginForm';
import { useDebounce } from '@/renderer/utils/hooks/useDebounce';
import { useIsFirstRender } from '@/renderer/utils/hooks/useIsFirstRender';
import { IS_SERVER } from '@/renderer/utils/ssr';
import useMe, { hasLikelyValidToken } from '@/renderer/utils/useMe';
import { LoadingIcon } from './UI/LoadingIcon';
import PageSuspense from './UI/PageSuspense';

export default function Guarded({
  children,
  subtitle,
  hideLogo,
  suspenseFallback = true,
}: {
  children: (React.ReactNode | JSX.Element)[] | React.ReactNode | JSX.Element;
  subtitle?: (React.ReactNode | JSX.Element)[] | React.ReactNode | JSX.Element | string;
  hideLogo?: boolean;
  suspenseFallback?: boolean | (React.ReactNode | JSX.Element)[] | React.ReactNode | JSX.Element;
}) {
  const [me, , refetchUser] = useMe(false, true);
  const isFirstRender = useIsFirstRender();
  const shouldInitiallyShowLoadingIcon = useDebounce(isFirstRender, 7500);

  if (me === undefined) {
    // We were not able to get a session yet. `undefined` means "unknown yet",
    // NOT "signed out" — on a cold native boot there's no SSR user and the
    // account loads async, so this is the normal in-flight state.
    const tokenLikelyValid = !IS_SERVER && hasLikelyValidToken();

    // With a valid token in localStorage this is almost always a slow/flaky
    // account fetch on a still-valid session — keep a calm reconnecting state
    // (never the red "Failed to load your account" error) and let
    // PageTokenRefresher's bounded retry recover. Also show the plain spinner
    // during the initial grace window even without a token, to avoid flashing an
    // error on a fast normal load.
    if (!IS_SERVER && (tokenLikelyValid || shouldInitiallyShowLoadingIcon)) {
      // Once the grace window elapses on a valid-token session, surface a gentle
      // "reconnecting" message and a manual retry instead of a bare spinner.
      const showReconnecting = tokenLikelyValid && !shouldInitiallyShowLoadingIcon;

      return (
        <div className="relative h-[stretch] w-[stretch]">
          <div className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 flex flex-col items-center gap-3 text-center">
            <LoadingIcon isLoading={true} className="size-6 data-[loading=true]:opacity-50" />
            {showReconnecting && (
              <>
                <p className="text-sm text-[CanvasText] opacity-70">
                  Reconnecting to your account…
                </p>
                <button
                  type="button"
                  onClick={() => refetchUser()}
                  className="text-sm underline opacity-70 hover:opacity-100"
                >
                  Retry now
                </button>
              </>
            )}
          </div>
        </div>
      );
    }

    // No valid token (and past the grace window) — genuinely no session to load.
    return (
      <LoginForm
        guarded={true}
        subtitle={
          <>
            <aside className="bg-red-50 dark:bg-red-950/50 p-4">
              <div className="flex flex-row items-center space-x-2 text-sm text-red-700 dark:text-red-200 text-left">
                <ExclamationCircleIcon height={24} className="size-6 shrink-0" aria-hidden="true" />
                <span>
                  Failed to load your account. Either RAS is undergoing maintenance, or your
                  connection is unstable. Try to{' '}
                  <a href="#refresh" onClick={() => location.reload()}>
                    refresh
                  </a>
                  , or sign in again below.
                </span>
              </div>
            </aside>
            <hr className="border-[CanvasText] opacity-25 my-3" />
            {subtitle}
          </>
        }
        hideLogo={hideLogo}
      />
    );
  }

  if (!me) {
    // Me = null, so this user is definitely not logged in
    return <LoginForm guarded={true} subtitle={subtitle} hideLogo={hideLogo} />;
  }

  if (suspenseFallback) {
    return <PageSuspense fallback={suspenseFallback}>{children}</PageSuspense>;
  }

  return children;
}
